import { Link } from 'react-router-dom';

import { LinkPanel } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { localizedPath } from '@/i18n/locale-path';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { DOC_SECTIONS, getDocPage } from './content/docs-nav';
import { DocsLayout } from './DocsLayout';
import { DocsSearch } from './DocsSearch';

import type { ReactElement, ReactNode } from 'react';

/**
 * A section heading on the index. Paired with a hairline that runs to the end of
 * the column, so stacked sections read as separated groups rather than as one
 * long ladder.
 */
function SectionHeading({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <h2 className="flex items-center gap-4 text-[1.2rem] font-semibold tracking-[-0.028em] text-[color:var(--bd-ink)]">
      <span className="flex-none">{children}</span>
      <span aria-hidden="true" className="h-px flex-1 bg-[color:var(--bd-line)]" />
    </h2>
  );
}

function CardTitle({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <span className="block font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
      {children}
    </span>
  );
}

function CardText({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <span className="mt-1.5 block text-[0.9rem] leading-[1.6] text-[color:var(--bd-muted)]">
      {children}
    </span>
  );
}

/**
 * The four journeys readers arrive with, in order: get running, put the chat
 * in your app, sign your users in, connect an AI client. Resolved through the
 * registry so a renamed slug fails loudly (the card simply disappears and the
 * docs-nav tests flag the missing page).
 */
const START_HERE_SLUGS = [
  'quickstart',
  'widget/embedding',
  'auth/end-user-auth',
  'mcp/connect',
] as const;

/**
 * `/docs` and `/<locale>/docs` index (ADR-0074/0075; site-wide locales ADR-0133).
 * A search box and the four highest-intent journeys up top, then every section
 * as a compact directory of its pages. The locale comes from the active i18n
 * context, which `LocaleSync` keeps pinned to the URL prefix.
 *
 * The directory below "Start here" is deliberately a LIST, not a card grid. It
 * used to render all 109 pages as description cards, which made the index
 * 11,840px tall — an undifferentiated wall a reader cannot scan, restating in
 * full what the sidebar beside it already lists. Every link is still present
 * (nothing became unreachable), but a title alone is enough for a directory: the
 * reader is looking for a name they already half-know, and the description is a
 * click away on the page itself.
 */
export function DocsIndex(): ReactElement {
  const { t, locale } = useT();

  useLocalizedSeoMeta({
    title: t('docs.index.metaTitle'),
    description: t('docs.index.metaDescription'),
    path: '/docs',
  });

  const startPages = START_HERE_SLUGS.map((slug) => getDocPage(slug)).filter(
    (p): p is NonNullable<typeof p> => p !== undefined,
  );

  return (
    <DocsLayout locale={locale}>
      <header className="mb-9 flex flex-col gap-3">
        <h1 className="text-[clamp(1.85rem,3.2vw,2.5rem)] font-semibold leading-[1.08] tracking-[-0.042em] text-[color:var(--bd-ink)]">
          {t('docs.index.title')}
        </h1>
        <p className="text-pretty text-[1.08rem] leading-[1.6] text-[color:var(--bd-muted)]">
          {t('docs.index.intro')}
        </p>
        <DocsSearch locale={locale} className="mt-2 max-w-xl lg:hidden" />
      </header>

      <section className="mb-11 flex flex-col gap-4">
        <SectionHeading>{t('docs.index.start.heading')}</SectionHeading>
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {startPages.map((page) => (
            <li key={page.slug}>
              {/* The four highest-intent journeys carry the brand rule the story
                  band's result cards use — the one device that marks a card as
                  "the thing you came for" without a second fill colour. */}
              <LinkPanel
                href={localizedPath(`/docs/${page.slug}`, locale)}
                accent
                className="p-5 pt-[21px]"
              >
                <CardTitle>{t(page.titleKey)}</CardTitle>
                <CardText>{t(page.descriptionKey)}</CardText>
              </LinkPanel>
            </li>
          ))}
        </ul>
      </section>

      {/* `columns` rather than a grid: a masonry-ish flow packs fourteen
          sections of wildly different length (2 pages to 20) without leaving a
          grid row as tall as its longest member. `break-inside-avoid` keeps a
          section whole across the column break. */}
      <div className="gap-x-10 sm:columns-2 xl:columns-3">
        {DOC_SECTIONS.map((section) => (
          <section key={section.id} className="mb-8 break-inside-avoid">
            <h2 className="font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
              {t(section.titleKey)}
            </h2>
            <ul className="mt-2.5 flex flex-col border-s border-[color:var(--bd-line)]">
              {section.pages.map((page) => (
                <li key={page.slug} className="-ms-px">
                  <Link
                    to={localizedPath(`/docs/${page.slug}`, locale)}
                    className="block border-s-2 border-transparent py-1.5 ps-3 text-[0.88rem] leading-[1.45] text-[color:var(--bd-body)] transition-colors duration-200 hover:border-[color:var(--bd-blue)] hover:text-[color:var(--bd-ink)] focus-visible:rounded-e-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)]"
                  >
                    {t(page.titleKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </DocsLayout>
  );
}
